/**
 * Discover crews.
 *
 * Rebuilt after review ("way more professional, clean and smooth"). It was a
 * 620px list of small rows; it is now a large panel of crew cards - each with
 * its banner, mark, name, description, tags, size, level and how you get in -
 * with search, a Top / Newest switch, and a join button on the card itself.
 * Cards fade up in sequence when the list arrives, and the panel eases in and
 * out rather than snapping.
 *
 * Opening a card still shows the full crew panel.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useBackdropClose } from '@/lib/backdrop';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Button, Empty, useToast } from './ui';
import { openCrew, type Crew } from './Crew';
import { CrewBanner, CrewMark, crewColor } from './CrewArt';

type Sort = 'score' | 'new';

export function FindCrew({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [typed, setTyped] = useState('');
  const [sort, setSort] = useState<Sort>('score');
  const backdrop = useBackdropClose(onClose);
  const me = useSession((s) => s.me);
  const inCrew = !!me?.crew;

  /* Search as you type, without a request per keystroke. */
  useEffect(() => {
    const t = window.setTimeout(() => setQ(typed.trim()), 220);
    return () => window.clearTimeout(t);
  }, [typed]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const crews = useQuery({
    queryKey: ['crews', q, sort],
    queryFn: () => api.get<{ crews: Crew[]; total: number }>(
      '/api/crews?sort=' + sort + (q ? '&q=' + encodeURIComponent(q) : '')),
    placeholderData: (prev) => prev
  });

  const rows = crews.data?.crews ?? [];

  return (
    <div
      data-keeps-card
      className="discover-backdrop fixed inset-0 z-[300] grid place-items-center bg-[rgba(20,16,12,.46)] p-4 backdrop-blur-[6px]"
      {...backdrop}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="discover-title"
        className="pugo-panel discover-panel flex h-[min(860px,92vh)] w-full max-w-[1120px] flex-col overflow-hidden rounded-[calc(24px*var(--radius-mul,1))] border border-line-soft bg-surface-0 shadow-[0_40px_90px_-20px_rgba(0,0,0,.45)]"
      >
        {/* ---- header ---- */}
        <header className="relative flex-none overflow-hidden border-b border-line-soft px-6 pt-6 pb-5 sm:px-8">
          <span aria-hidden className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[radial-gradient(circle,color-mix(in_srgb,var(--accent)_22%,transparent),transparent_70%)]" />
          <span aria-hidden className="pointer-events-none absolute -bottom-28 left-10 h-56 w-56 rounded-full bg-[radial-gradient(circle,color-mix(in_srgb,#f5b83d_18%,transparent),transparent_70%)]" />

          <div className="relative flex items-start gap-4">
            <div className="min-w-0 flex-1">
              <p className="m-0 text-[13px] font-semibold tracking-[.12em] text-accent uppercase">Crews</p>
              <h2 id="discover-title" className="m-0 mt-1 text-[30px] leading-tight font-extrabold tracking-[-.03em] text-ink sm:text-[34px]">Discover crews</h2>
              <p className="m-0 mt-1 text-[16px] text-dim">Find your people. Every crew has its own events, voice room and tag.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-2 text-dim outline-none transition-[background-color,color,transform] duration-[var(--t-hover)] hover:bg-surface-3 hover:text-ink active:scale-95 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              <Icon name="close" size={18} />
            </button>
          </div>

          <div className="relative mt-5 flex flex-wrap items-center gap-3">
            <label className="relative min-w-[240px] flex-1">
              <span className="sr-only">Search crews</span>
              <Icon name="search" size={18} className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-mute" />
              <input
                autoFocus
                type="search"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="Search by name or tag…"
                spellCheck={false}
                className="h-[48px] w-full rounded-[calc(14px*var(--radius-mul,1))] border border-line-soft bg-surface-1 pr-4 pl-11 text-[15px] text-ink shadow-[var(--shadow-1)] outline-none transition-[border-color,box-shadow] duration-[var(--t-hover)] placeholder:text-ghost focus:border-accent focus:shadow-[0_0_0_4px_var(--accent-soft)]"
              />
            </label>

            <div role="radiogroup" aria-label="Order" className="flex rounded-[calc(14px*var(--radius-mul,1))] border border-line-soft bg-surface-1 p-1 shadow-[var(--shadow-1)]">
              {([['score', 'Top crews', 'trending'], ['new', 'Newest', 'sparkle']] as const).map(([id, label, icon]) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={sort === id}
                  onClick={() => setSort(id)}
                  className={cn(
                    'flex h-[38px] items-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] px-4 text-[14px] font-semibold outline-none',
                    'transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                    sort === id ? 'bg-accent text-accent-fg' : 'text-dim hover:text-ink'
                  )}
                >
                  <Icon name={icon} size={16} />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </header>

        {/* ---- the crews ---- */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-surface-inset/40 px-6 py-6 sm:px-8">
          {crews.isPending ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="h-[300px] animate-pulse rounded-[calc(20px*var(--radius-mul,1))] bg-surface-2" />
              ))}
            </div>
          ) : crews.isError ? (
            <Empty role="alert" title="Crews could not load" note="Check your connection and try again." actions={<Button kind="outline" onClick={() => void crews.refetch()}>Try again</Button>} />
          ) : rows.length ? (
            <>
              <p className="m-0 mb-4 text-[14px] text-mute" aria-live="polite">
                {q ? rows.length + ' matching "' + q + '"' : (crews.data?.total ?? rows.length) + ' crews'}
              </p>
              <ul className={cn('m-0 grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3 transition-opacity duration-200', crews.isFetching && 'opacity-70')}>
                {rows.map((crew, i) => (
                  <CrewCard key={crew.id} crew={crew} index={i} inCrew={inCrew} sort={sort} onOpen={() => { onClose(); openCrew(crew.tag); }} />
                ))}
              </ul>
            </>
          ) : (
            <Empty
              icon="search"
              title={q ? 'No crews match "' + q + '"' : 'No crews yet'}
              note={q ? 'Try a different name or tag.' : 'Somebody has to be first.'}
            />
          )}
        </div>
      </div>

      <style>{`
        @keyframes discover-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes discover-rise { from { opacity: 0; transform: translateY(14px) scale(.985) } to { opacity: 1; transform: none } }
        .discover-backdrop { animation: discover-fade 220ms ease-out both }
        .discover-panel { animation: discover-rise 300ms cubic-bezier(.2,.8,.2,1) both }
        .discover-card { animation: discover-rise 360ms cubic-bezier(.2,.8,.2,1) both }
        @media (prefers-reduced-motion: reduce) { .discover-backdrop, .discover-panel, .discover-card { animation: none } }
      `}</style>
    </div>
  );
}

const JOIN: Record<Crew['settings']['join'], { label: string; tone: string }> = {
  open: { label: 'Open', tone: 'bg-[color-mix(in_srgb,var(--good)_14%,transparent)] text-good' },
  request: { label: 'Request to join', tone: 'bg-[color-mix(in_srgb,var(--accent)_12%,transparent)] text-accent' },
  invite: { label: 'Invite only', tone: 'bg-surface-3 text-dim' }
};

function CrewCard({ crew, index, inCrew, sort, onOpen }: {
  crew: Crew;
  index: number;
  inCrew: boolean;
  sort: Sort;
  onOpen: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const colour = crewColor(crew.appearance);
  const join = JOIN[crew.settings.join];
  const mine = !!crew.myRole;

  async function joinCrew(e: React.MouseEvent) {
    e.stopPropagation();
    setBusy(true);
    try {
      const res = await api.post<{ pending: boolean }>('/api/crews/' + crew.id + '/join', {});
      toast(res.pending ? 'Request sent to ' + crew.name : 'Welcome to ' + crew.name, 'good');
      void queryClient.invalidateQueries({ queryKey: ['crews'] });
      void useSession.getState().refresh();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="discover-card" style={{ animationDelay: Math.min(index, 12) * 40 + 'ms' }}>
      <div
        className={cn(
          'pugo-panel group relative flex h-full flex-col overflow-hidden rounded-[calc(20px*var(--radius-mul,1))] border border-line-soft bg-surface-1',
          'transition-[transform,box-shadow,border-color] duration-[250ms] ease-[cubic-bezier(.2,.8,.2,1)]',
          'hover:-translate-y-1 hover:border-line hover:shadow-[0_18px_40px_-18px_rgba(0,0,0,.35)] motion-reduce:hover:translate-y-0'
        )}
      >
        <button type="button" onClick={onOpen} aria-label={'Open ' + crew.name} className="absolute inset-0 z-0 rounded-[inherit] outline-none focus-visible:shadow-[inset_0_0_0_3px_var(--accent)]" />

        <div className="pointer-events-none relative h-[108px] flex-none overflow-hidden">
          <CrewBanner appearance={crew.appearance} className="h-full w-full transition-transform duration-500 ease-out group-hover:scale-[1.04] motion-reduce:group-hover:scale-100" />
          <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[rgba(0,0,0,.25)] to-transparent" />
          {sort === 'score' && crew.rank && crew.rank <= 3 ? (
            <span className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[12.5px] font-bold text-white backdrop-blur-sm">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="#f5b83d" aria-hidden="true"><path d="M3.4 7.6 7 12l5-6.6L17 12l3.6-4.4-1.4 11H4.8Z" /></svg>
              #{crew.rank}
            </span>
          ) : null}
        </div>

        <div className="pointer-events-none relative flex flex-1 flex-col px-5 pb-5">
          <div className="-mt-9 flex items-end gap-3">
            <CrewMark tag={crew.tag} appearance={crew.appearance} size={68} className="rounded-[calc(16px*var(--radius-mul,1))] shadow-[0_8px_20px_-8px_rgba(0,0,0,.45)] ring-4 ring-[var(--bg-1)]" />
            <span className="mb-1 ml-auto flex items-baseline gap-1 rounded-full bg-surface-2 px-2.5 py-1">
              <span className="text-[12px] font-semibold text-mute">Level</span>
              <span className="text-[16px] leading-none font-extrabold tabular-nums" style={{ color: colour }}>{crew.level?.level ?? 1}</span>
            </span>
          </div>

          <h3 className="m-0 mt-3 truncate text-[19px] font-bold tracking-[-.01em] text-ink">{crew.name}</h3>
          <p className="m-0 mt-0.5 flex items-center gap-2 text-[13.5px] text-mute">
            <span className="font-mono font-bold" style={{ color: colour }}>{crew.tag}</span>
            <span aria-hidden>·</span>
            <span>{crew.memberCount} {crew.memberCount === 1 ? 'member' : 'members'}</span>
          </p>
          <p className={cn('m-0 mt-2.5 line-clamp-2 text-[14.5px] leading-relaxed', crew.description ? 'text-dim' : 'text-ghost')}>
            {crew.description || 'No description yet.'}
          </p>

          {crew.tags?.length ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {crew.tags.slice(0, 3).map((t) => <span key={t} className="rounded-full bg-surface-2 px-2.5 py-1 text-[12.5px] font-medium text-dim">{t}</span>)}
            </div>
          ) : null}

          <div className="mt-auto flex items-center gap-2 pt-4">
            <span className={cn('rounded-full px-2.5 py-1 text-[12.5px] font-semibold', join.tone)}>{join.label}</span>
            <span className="flex-1" />
            {mine ? (
              <span className="text-[14px] font-semibold text-good">Your crew</span>
            ) : !inCrew && crew.settings.join !== 'invite' ? (
              <span className="pointer-events-auto relative z-[1]">
                <Button kind="primary" size="sm" disabled={busy} onClick={(e) => void joinCrew(e)}>
                  {crew.settings.join === 'open' ? 'Join' : 'Ask to join'}
                </Button>
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[14px] font-semibold text-accent">View <Icon name="arrowR" size={15} /></span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
